/**
 * General ledger API surface (M2).
 *
 * Money is a string end to end, same as invoicing: the API sends a Postgres NUMERIC as
 * text, and a balance that has been through a double is not a balance any more. The one
 * place it becomes a number is `formatMoney`.
 *
 * Note the asymmetry with the rest of the app: there is no update and no delete for a
 * journal entry at any permission level. The server refuses both, because a posted line is
 * immutable — the correction for a wrong entry is a reversal, which is why `reverseEntry`
 * is the only mutation on this file that touches something already posted.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'
import { formatAmount } from '@/lib/money'

export const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'] as const

export type AccountType = (typeof ACCOUNT_TYPES)[number]

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Revenue',
  expense: 'Expense',
}

/** Which side increases the account. Mirrors NORMAL_BALANCE in the API's `coa.py`. */
export const NORMAL_BALANCE: Record<AccountType, 'debit' | 'credit'> = {
  asset: 'debit',
  liability: 'credit',
  equity: 'credit',
  revenue: 'credit',
  expense: 'debit',
}

/**
 * What raised an entry. Mirrors `ENTRY_SOURCES` in the API's `accounting/models.py`, which
 * a check constraint enforces — a value missing here is a filter option the journal cannot
 * offer and a "Raised by" cell that prints a bare slug.
 *
 * `pos` is the older bare value; `pos_transaction` and `pos_store_credit_entry` are what the
 * POS consumers actually post with today. Both are kept so rows written under either still
 * read.
 */
export const ENTRY_SOURCES = [
  'manual',
  'invoice',
  'payment',
  'stock',
  'pos',
  'pos_transaction',
  'pos_store_credit_entry',
  'payroll',
  'commission',
] as const

export type EntrySource = (typeof ENTRY_SOURCES)[number]

export const SOURCE_LABELS: Record<EntrySource, string> = {
  manual: 'Manual',
  invoice: 'Invoice',
  payment: 'Payment',
  stock: 'Stock',
  pos: 'POS',
  pos_transaction: 'POS sale',
  pos_store_credit_entry: 'Store credit',
  payroll: 'Payroll',
  commission: 'Commission',
}

export type EntryStatus = 'posted' | 'reversed' | 'reversal'

export interface Account {
  id: string
  code: string
  name: string
  type: AccountType
  parent_id: string | null
  is_postable: boolean
  is_active: boolean
  description: string | null
  created_at: string
  updated_at: string
}

export interface AccountInput {
  code: string
  name: string
  type: AccountType
  parent_id?: string | null
  is_postable: boolean
  description?: string | null
}

export interface AccountPatch {
  code?: string
  name?: string
  parent_id?: string | null
  is_postable?: boolean
  is_active?: boolean
  description?: string | null
}

export interface Period {
  id: string
  name: string
  start_date: string
  end_date: string
  status: 'open' | 'closed'
  closed_at: string | null
  notes: string | null
}

export interface PostingRule {
  id: string
  event_type: string
  name: string
  debit_account_id: string
  credit_account_id: string
  amount_field: string
  match_key: string | null
  match_value: string | null
  sequence: number
  is_active: boolean
}

export interface JournalLine {
  id: string
  line_number: number
  account_id: string
  debit: string
  credit: string
  description: string | null
  account_code: string | null
  account_name: string | null
}

export interface JournalEntrySummary {
  id: string
  entry_number: string
  entry_date: string
  description: string
  source_type: EntrySource
  source_id: string | null
  status: EntryStatus
  total_debit: string
  total_credit: string
  created_at: string
}

export interface JournalEntry extends JournalEntrySummary {
  period_id: string
  event_id: string | null
  reversal_of_id: string | null
  posted_by_id: string | null
  memo: string | null
  lines: JournalLine[]
}

export interface JournalLineInput {
  account_id: string
  debit: string
  credit: string
  description?: string | null
}

export interface JournalEntryInput {
  entry_date: string
  description: string
  lines: JournalLineInput[]
  memo?: string | null
}

export interface BalanceRow {
  account_id: string
  code: string
  name: string
  type: AccountType
  debit: string
  credit: string
  balance: string
}

export interface TrialBalance {
  as_of: string
  rows: BalanceRow[]
  total_debit: string
  total_credit: string
  /** False is a bug in the ledger, not in the report — the screen says so loudly. */
  balanced: boolean
}

export interface ProfitAndLoss {
  start_date: string
  end_date: string
  revenue: BalanceRow[]
  expenses: BalanceRow[]
  total_revenue: string
  total_expenses: string
  net_profit: string
}

export interface LedgerLine {
  entry_id: string
  entry_number: string
  entry_date: string
  description: string
  debit: string
  credit: string
  balance: string
}

export interface GeneralLedger {
  account: Account
  start_date: string | null
  end_date: string
  opening_balance: string
  closing_balance: string
  lines: LedgerLine[]
}

const BASE = `${API_PREFIX}/accounting`

/**
 * The whole chart in one value. A tree needs every node -- a child whose parent fell off
 * the end of the page has nowhere to be drawn -- so this follows the pages instead of
 * asking for one enormous one. `page_size` is capped at 200 server-side, and the seed
 * ships 36 accounts, so today this is a single request; a distributor with a real chart
 * of accounts was previously losing everything past the two hundredth row in silence.
 *
 * The returned `meta` is the first page's, and describes the request rather than the
 * value. Callers read `data`; the screen pages what it was handed.
 */
export const listAccounts = async (
  params: { include_inactive?: boolean } = {},
): Promise<Page<Account>> => {
  const suffix = params.include_inactive === false ? '&include_inactive=false' : ''
  const first = await apiFetch<Page<Account>>(`${BASE}/accounts?page=1&page_size=200${suffix}`)

  const accounts = [...first.data]
  for (let page = 2; page <= first.meta.total_pages; page += 1) {
    const next = await apiFetch<Page<Account>>(
      `${BASE}/accounts?page=${page}&page_size=200${suffix}`,
    )
    accounts.push(...next.data)
  }

  return { ...first, data: accounts }
}

export const createAccount = (input: AccountInput) =>
  apiGet<Account>(`${BASE}/accounts`, { method: 'POST', body: JSON.stringify(input) })

export const updateAccount = (id: string, patch: AccountPatch) =>
  apiGet<Account>(`${BASE}/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })

export const deleteAccount = (id: string) =>
  apiFetch<void>(`${BASE}/accounts/${id}`, { method: 'DELETE' })

export const listPostingRules = () =>
  apiFetch<Page<PostingRule>>(`${BASE}/posting-rules?page_size=200`)

export const listPeriods = () => apiFetch<Page<Period>>(`${BASE}/periods?page_size=200`)

export const closePeriod = (id: string, notes?: string) =>
  apiGet<Period>(`${BASE}/periods/${id}/close`, {
    method: 'POST',
    body: JSON.stringify({ notes: notes ?? null }),
  })

export const reopenPeriod = (id: string, notes?: string) =>
  apiGet<Period>(`${BASE}/periods/${id}/reopen`, {
    method: 'POST',
    body: JSON.stringify({ notes: notes ?? null }),
  })

export interface JournalFilters {
  page?: number
  q?: string
  source_type?: EntrySource | ''
  account_id?: string
  date_from?: string
  date_to?: string
  period_id?: string
  location_id?: string
}

function query(filters: JournalFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.q) params.set('q', filters.q)
  if (filters.source_type) params.set('source_type', filters.source_type)
  if (filters.account_id) params.set('account_id', filters.account_id)
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  if (filters.period_id) params.set('period_id', filters.period_id)
  if (filters.location_id) params.set('location_id', filters.location_id)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listEntries = (filters: JournalFilters = {}) =>
  apiFetch<Page<JournalEntrySummary>>(`${BASE}/journal-entries${query(filters)}`)

export const getEntry = (id: string) => apiGet<JournalEntry>(`${BASE}/journal-entries/${id}`)

export const createEntry = (input: JournalEntryInput) =>
  apiGet<JournalEntry>(`${BASE}/journal-entries`, { method: 'POST', body: JSON.stringify(input) })

/** The only way to correct a posted entry. Returns the *new* mirror entry, not the original. */
export const reverseEntry = (id: string, reason: string, onDate?: string) =>
  apiGet<JournalEntry>(`${BASE}/journal-entries/${id}/reverse`, {
    method: 'POST',
    body: JSON.stringify({ reason, on_date: onDate ?? null }),
  })

export const exportEntries = (filters: JournalFilters, format: ExportFormat) => {
  const qs = query(filters)
  return apiBlob(`${BASE}/journal-entries/export${qs ? `${qs}&fmt=${format}` : `?fmt=${format}`}`)
}

export const getTrialBalance = (asOf?: string, locationId?: string) => {
  const params = new URLSearchParams()
  if (asOf) params.set('as_of', asOf)
  if (locationId) params.set('location_id', locationId)
  const qs = params.toString()
  return apiGet<TrialBalance>(`${BASE}/reports/trial-balance${qs ? `?${qs}` : ''}`)
}

export const exportTrialBalance = (format: ExportFormat, asOf?: string, locationId?: string) => {
  const params = new URLSearchParams({ fmt: format })
  if (asOf) params.set('as_of', asOf)
  if (locationId) params.set('location_id', locationId)
  return apiBlob(`${BASE}/reports/trial-balance/export?${params}`)
}

export const getProfitAndLoss = (start: string, end: string, locationId?: string) => {
  const params = new URLSearchParams({ start, end })
  if (locationId) params.set('location_id', locationId)
  return apiGet<ProfitAndLoss>(`${BASE}/reports/profit-and-loss?${params}`)
}

export const getGeneralLedger = (
  accountId: string,
  start?: string,
  end?: string,
  locationId?: string,
) => {
  const params = new URLSearchParams()
  if (start) params.set('start', start)
  if (end) params.set('end', end)
  if (locationId) params.set('location_id', locationId)
  const qs = params.toString()
  return apiGet<GeneralLedger>(`${BASE}/reports/general-ledger/${accountId}${qs ? `?${qs}` : ''}`)
}

/** Rupees, en-IN grouping. Lives in `@/lib/money` so every screen prints one format. */
export { formatMoney } from '@/lib/money'

/**
 * A zero side renders as nothing rather than 0.00.
 *
 * Every ledger line carries both columns and one of them is always zero. Printing it fills
 * the table with noise and makes the column that matters harder to scan.
 */
export function formatSide(amount: string): string {
  return Number(amount) === 0 ? '' : formatAmount(amount)
}

/** First day of the month containing `on`, as YYYY-MM-DD. Used for the report defaults. */
export function startOfMonth(on = new Date()): string {
  return `${on.getFullYear()}-${String(on.getMonth() + 1).padStart(2, '0')}-01`
}

export function today(on = new Date()): string {
  return `${on.getFullYear()}-${String(on.getMonth() + 1).padStart(2, '0')}-${String(
    on.getDate(),
  ).padStart(2, '0')}`
}

/**
 * Nest a flat account list by `parent_id`, in code order.
 *
 * Depth is carried on the row rather than as nested children: the chart renders as a table
 * with an indent, and a tree of arrays would mean flattening it again to lay out rows.
 */
export interface AccountNode extends Account {
  depth: number
}

export function toTree(accounts: Account[]): AccountNode[] {
  const byParent = new Map<string | null, Account[]>()
  for (const account of accounts) {
    const siblings = byParent.get(account.parent_id) ?? []
    siblings.push(account)
    byParent.set(account.parent_id, siblings)
  }
  for (const siblings of byParent.values()) {
    siblings.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
  }

  const known = new Set(accounts.map((account) => account.id))
  const out: AccountNode[] = []

  const walk = (parent: string | null, depth: number) => {
    for (const account of byParent.get(parent) ?? []) {
      out.push({ ...account, depth })
      walk(account.id, depth + 1)
    }
  }
  walk(null, 0)

  // An account whose parent is filtered out of the page would otherwise vanish entirely.
  // Show it at the root rather than dropping it — a missing account reads as data loss.
  for (const account of accounts) {
    if (account.parent_id && !known.has(account.parent_id)) out.push({ ...account, depth: 0 })
  }

  return out
}
